import { RefObject, useEffect } from 'react';

/**
 * Scrolls the given ref into view (centered, smooth) after data loads with data or on error.
 * Cleans up any pending timeouts on unmount or dependency change.
 *
 * @param ref - React ref to the element to scroll into view
 * @param isLoading - Boolean indicating loading state
 * @param hasData - Boolean indicating if data is present (e.g., data && data.length > 0)
 * @param isError - Boolean indicating if there was an error loading data
 * @param delay - Optional delay in ms before scrolling (default: 400)
 */
export function useScrollIntoViewOnData<T extends HTMLElement>(
  ref: RefObject<T | null>,
  isLoading: boolean,
  hasData: boolean,
  isError: boolean,
  delay = 400,
) {
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (!isLoading && (hasData || isError) && ref.current) {
      timeout = setTimeout(() => {
        ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, delay);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading, hasData, isError, ref, delay]);
}
